'use client';

import ArrowForward from '@mui/icons-material/ArrowForward';
import {
  Box,
  Button,
  Container,
  Grid,
  InputAdornment,
  InputBase,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import CompanyRatingBadge from '@/components/Reviews/ReviewIO';
import { buildUrlWithAmount, formatNumberWithCommas } from '@/lib/helpers/formatHelpers';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { getPageCodeFromPath } from '@/lib/helpers/pageHelpers';
import { buildUrlWithUtmParams } from '@/lib/helpers/utmParamHelpers';
import { ComponentBlocksButton, ComponentBlocksHeroForm } from '@/types';

const CTAWrapper = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: 1.5,
      maxWidth: 900,
      width: '100%',
      flexWrap: { xs: 'wrap', sm: 'nowrap' },
    }}
  >
    {children}
  </Box>
);

// All CMS-controllable CTA variants below use a mounted guard (render placeholder until
// useEffect runs) so MUI InputBase/Button DOM matches between server and client and avoids hydration errors.
// If you add a new CTA type that uses InputBase, TextField, or other MUI inputs, use the same pattern.

export const NumberInputCTA = ({
  cta,
  rawAmount,
  setRawAmount,
}: {
  cta: ComponentBlocksButton;
  rawAmount: string;
  setRawAmount: (_val: string) => void;
}) => {
  const theme = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const pathname = usePathname();

  const {
    ButtonLink = process.env.NEXT_PUBLIC_FULL_APP_URL,
    ButtonPlaceholder = '3,000.00',
    ButtonText = 'Get started',
  } = cta;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/,/g, '').replace(/[^0-9.]/g, '');
    const parts = input.split('.');
    if (parts.length > 2 || parts[1]?.length > 2) return;
    const formatted = formatNumberWithCommas(input);
    setRawAmount(formatted);
  };

  const finalHref = useMemo(() => {
    const fullUrl =
      ButtonLink?.includes('apply.lend') && rawAmount
        ? buildUrlWithAmount(ButtonLink, rawAmount.replace(/,/g, ''))
        : (ButtonLink ?? '#');
    return buildUrlWithUtmParams(fullUrl, getPageCodeFromPath(pathname));
  }, [ButtonLink, rawAmount, pathname]);

  if (!mounted) {
    return (
      <CTAWrapper>
        <Box
          sx={{
            flex: 1,
            height: '3rem',
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.2)',
          }}
          aria-hidden
        />
        <Button disabled sx={{ height: '3rem', px: 3 }}>
          {ButtonText}
        </Button>
      </CTAWrapper>
    );
  }

  return (
    <CTAWrapper>
      <InputBase
        placeholder={ButtonPlaceholder ?? ''}
        value={rawAmount}
        onChange={handleChange}
        inputProps={{
          inputMode: 'decimal',
          pattern: '[0-9.]*',
          'aria-label': 'Amount',
        }}
        startAdornment={
          <InputAdornment position="start">
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: theme.palette.purple[600],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: theme.palette.common.white,
                  fontSize: '0.95rem',
                  textAlign: 'center',
                }}
              >
                $
              </Typography>
            </Box>
          </InputAdornment>
        }
        sx={{
          flex: 1,
          px: 2.5,
          fontSize: '0.9rem',
          color: '#333',
          height: '3rem',
          borderRadius: 2,
          backgroundColor: '#fff',
          boxShadow: '0 0.125rem 0.25rem rgba(0,0,0,0.08)',
          '& input': { padding: 0 },
        }}
      />
      <Button
        component={Link}
        href={finalHref}
        target="_blank"
        rel="noopener noreferrer"
        variant="contained"
        color="primary"
        endIcon={<ArrowForward />}
        sx={{
          height: '3rem',
          px: 3,
          fontSize: '0.9rem',
          whiteSpace: 'nowrap',
          '@media (max-width: 600px)': {
            width: '100%',
            justifyContent: 'center',
          },
        }}
      >
        {ButtonText}
      </Button>
    </CTAWrapper>
  );
};

export const EmailInputCTA = ({ cta }: { cta: ComponentBlocksButton }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const pathname = usePathname();
  const [email, setEmail] = useState('');

  const finalHref = useMemo(() => {
    const baseUrl = buildUrlWithUtmParams(
      cta.ButtonLink ?? 'https://partners.lend.com.au/partners/register',
      getPageCodeFromPath(pathname)
    );

    const trimmedEmail = email.trim();
    if (!trimmedEmail) return baseUrl;

    try {
      const url = new URL(baseUrl);
      url.searchParams.set('email', trimmedEmail);
      return url.toString();
    } catch {
      return baseUrl;
    }
  }, [cta, pathname, email]);

  if (!mounted) {
    return (
      <CTAWrapper>
        <Box
          sx={{
            flex: 1,
            height: '3rem',
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.2)',
          }}
          aria-hidden
        />
        <Button disabled sx={{ height: '3rem', px: 3 }} endIcon={<ArrowForward />}>
          Get started
        </Button>
      </CTAWrapper>
    );
  }
  return (
    <CTAWrapper>
      <InputBase
        type="email"
        placeholder={cta.ButtonPlaceholder || 'Enter your work email'}
        value={email}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
        inputProps={{ 'aria-label': 'Email Address' }}
        sx={{
          flex: 1,
          px: 2.5,
          fontSize: '0.9rem',
          color: '#000',
          height: '3rem',
          borderRadius: 2,
          backgroundColor: '#fff',
          boxShadow: '0 0.125rem 0.25rem rgba(0,0,0,0.08)',
          '& input': { padding: 0 },
        }}
      />
      <Button
        variant="contained"
        color="primary"
        endIcon={<ArrowForward />}
        component="a"
        href={finalHref}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          height: '3rem',
          px: 3,
          fontSize: '0.9rem',
          whiteSpace: 'nowrap',
          '@media (max-width: 600px)': {
            width: '100%',
            justifyContent: 'center',
          },
        }}
      >
        {cta.ButtonText || 'Get access now'}
      </Button>
    </CTAWrapper>
  );
};

export const PlainButtonCTA = ({ cta }: { cta: ComponentBlocksButton }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const pathname = usePathname();

  const finalHref = useMemo(() => {
    const currentPageCode = getPageCodeFromPath(pathname);
    const baseUrl = cta.ButtonLink ?? '#';
    return buildUrlWithUtmParams(baseUrl, currentPageCode);
  }, [cta, pathname]);

  if (!mounted) {
    return (
      <Button
        disabled
        sx={{
          height: '3rem',
          px: 3,
          fontSize: '0.9rem',
          whiteSpace: 'nowrap',
          '@media (max-width: 600px)': {
            width: '100%',
            justifyContent: 'center',
          },
        }}
      >
        {cta.ButtonText || 'Get access now'}
      </Button>
    );
  }
  return (
    <Button
      component="a"
      href={finalHref}
      target="_blank"
      rel="noopener noreferrer"
      variant="contained"
      color="primary"
      endIcon={<ArrowForward />}
      sx={{
        height: '3rem',
        px: 3,
        fontSize: '0.9rem',
        whiteSpace: 'nowrap',
        '@media (max-width: 600px)': {
          width: '100%',
          justifyContent: 'center',
        },
      }}
    >
      {cta.ButtonText || 'Get access now'}
    </Button>
  );
};

interface HeroProps extends ComponentBlocksHeroForm {
  Breadcrumbs?: ReactNode;
}

export default function Hero({
  HeroText,
  HeroBackgroundImage,
  HeroCTA = [],
  HeroMedia,
  HeroMediaDisplay,
  ShowReview,
  Breadcrumbs,
}: HeroProps) {
  const theme = useTheme();
  const bgImage =
    getImageUrl({ image: HeroBackgroundImage, size: 'url' }) || '/assets/placeholder-image.png';
  const [isClient, setIsClient] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [rawAmount, setRawAmount] = useState('50,000');

  const visibleCTAs = useMemo(
    () => HeroCTA?.filter((c): c is ComponentBlocksButton => Boolean(c?.ShowButton)) || [],
    [HeroCTA]
  );

  useEffect(() => {
    if (visibleCTAs?.[0]?.ButtonPlaceholder) {
      setRawAmount(visibleCTAs?.[0]?.ButtonPlaceholder);
    }
  }, [HeroCTA, visibleCTAs]);

  useEffect(() => setIsClient(true), []);

  const heroMediaUrl = getImageUrl({ image: HeroMedia, size: 'url' });
  const mediaElement = useMemo(() => {
    if (!heroMediaUrl) return null;
    const isVideo =
      HeroMedia?.mime?.startsWith('video/') ||
      heroMediaUrl.endsWith('.mp4') ||
      heroMediaUrl.endsWith('.mov');
    const boxBase = {
      position: 'relative' as const,
      width: '100%',
      height: '100%',
      borderRadius: '1.5rem',
      overflow: 'hidden',
      boxShadow: '0 0 40px rgba(0,0,0,0.2)',
      background: alpha(theme.palette.common.black, 0.1),
    };

    return (
      <Box sx={boxBase}>
        {isVideo && isClient && !videoError ? (
          <video
            src={heroMediaUrl}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onError={() => setVideoError(true)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: HeroMediaDisplay === 'cover' ? 'cover' : 'contain',
            }}
          />
        ) : (
          <Image
            src={heroMediaUrl}
            alt={HeroMedia?.alternativeText || 'Hero media'}
            width={800}
            height={500}
            priority
            fetchPriority="high"
            style={{
              width: '100%',
              height: '100%',
              objectFit: HeroMediaDisplay === 'cover' ? 'cover' : 'contain',
            }}
          />
        )}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.3), rgba(0,0,0,0))',
          }}
        />
      </Box>
    );
  }, [HeroMedia, heroMediaUrl, HeroMediaDisplay, isClient, videoError, theme]);

  return (
    <Box
      sx={{
        position: 'relative',
        color: 'white',
        px: { xs: 2, md: 4 },
        py: { xs: 5, md: 8 },
        display: 'flex',
        alignItems: 'center',
        backgroundImage: bgImage
          ? `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.65)), url(${bgImage})`
          : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Container maxWidth="lg">
        {Breadcrumbs}
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={7}>
            <Stack direction="column" spacing={3}>
              <GlobalBlockRenderer content={HeroText} textColor={bgImage ? 'white' : 'black'} />
              <Stack direction="column" spacing={2}>
                {visibleCTAs.length > 0 ? (
                  visibleCTAs.map((cta, i) =>
                    cta.ButtonNumberValue ? (
                      <NumberInputCTA
                        key={cta.id || i}
                        cta={cta}
                        rawAmount={rawAmount}
                        setRawAmount={setRawAmount}
                      />
                    ) : (
                      <EmailInputCTA key={cta.id || i} cta={cta} />
                    )
                  )
                ) : (
                  <NumberInputCTA
                    cta={
                      {
                        ButtonText: 'Get started',
                        ButtonPlaceholder: '3,000.00',
                      } as ComponentBlocksButton
                    }
                    rawAmount={rawAmount}
                    setRawAmount={setRawAmount}
                  />
                )}
              </Stack>
              {ShowReview && <CompanyRatingBadge />}
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            {mediaElement}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
